import { DatePickerRange } from '../../components';
import {
  ApiSuccess,
  CursorPagination,
  ExternalCallType,
  FilePurpose,
  IntegrationCall,
  IntegrationService,
  ProspectCategory,
  ProspectType,
} from '../../types';
import { apiSlice } from '../api.services';

interface GenerateOauthStatePayload {
  service: IntegrationService;
}

interface GenerateOAuthStateResponseData {
  state: string;
}

interface DisconnectIntegrationPayload {
  service: IntegrationService;
}

interface SubmitSalesloftAPIKeyPayload {
  service: IntegrationService.SALESLOFT;
  apiKey: string;
}

interface SubmitGongAPIKeyPayload {
  service: IntegrationService.GONG;
  accessKey: string;
  secretKey: string;
  apiBaseUrl: string;
}

type GetIntegrationCallsPayload = {
  callId?: string;
  startTime?: DatePickerRange;
  userIds?: string[];
  cursor?: string;
} & (
  | {
      service: IntegrationService.SALESLOFT;
      callType?: ExternalCallType;
    }
  | {
      service: IntegrationService.GONG;
    }
);

type GetIntegrationCallsResponseData = {
  calls: IntegrationCall[];
  pagination: CursorPagination;
  service: IntegrationService;
};

type BaseImportIntegrationCallsPayload = {
  externalCallIds: string[];
  service: IntegrationService;
  callType?: ExternalCallType;
};

// Both category and type are required for prospect creation.
type ImportCallsForProspectCreationPayload = BaseImportIntegrationCallsPayload & {
  purpose: FilePurpose.PROSPECT_CREATION;
  prospectCategory: ProspectCategory;
  prospectType: ProspectType;
};

type ImportCallsForCallCreationPayload = BaseImportIntegrationCallsPayload & {
  purpose: FilePurpose.CALL_CREATION;
  prospectCategory?: never;
  prospectType?: never;
};

const enhancedApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getIntegrationCalls: builder.mutation<GetIntegrationCallsResponseData, GetIntegrationCallsPayload>({
      query: ({ cursor, service, ...filters }) => ({
        url: `/integrations/${service.toLowerCase()}/calls`,
        method: 'POST',
        body: filters,
        params: { cursor },
      }),
      transformResponse: (response: ApiSuccess<GetIntegrationCallsResponseData>) => response.data,
    }),
    importIntegrationCalls: builder.mutation<
      ApiSuccess,
      ImportCallsForProspectCreationPayload | ImportCallsForCallCreationPayload
    >({
      query: (payload) => ({
        url: `/integrations/${payload.service.toLowerCase()}/calls/import`,
        method: 'POST',
        body: payload,
      }),
    }),
    generateIntegrationOauthState: builder.mutation<
      ApiSuccess<GenerateOAuthStateResponseData>,
      GenerateOauthStatePayload
    >({
      query: (payload) => ({
        url: `/integrations/${payload.service}/oauth-state`,
        method: 'POST',
      }),
    }),
    submitGongAPIKey: builder.mutation<ApiSuccess, SubmitGongAPIKeyPayload>({
      query: (payload) => ({
        url: `/integrations/${payload.service.toLowerCase()}/client-credentials`,
        method: 'POST',
        body: payload,
      }),
    }),

    submitSalesloftAPIKey: builder.mutation<ApiSuccess, SubmitSalesloftAPIKeyPayload>({
      query: (payload) => ({
        url: `/integrations/${payload.service.toLowerCase()}/api-key`,
        method: 'POST',
        body: payload,
      }),
    }),
    disconnectIntegration: builder.mutation<ApiSuccess, DisconnectIntegrationPayload>({
      query: ({ service }) => ({
        url: `/integrations/${service.toLowerCase()}/disconnect`,
        method: 'DELETE',
      }),
    }),
  }),
});

export const {
  useGetIntegrationCallsMutation,
  useImportIntegrationCallsMutation,
  useGenerateIntegrationOauthStateMutation,
  useSubmitGongAPIKeyMutation,
  useSubmitSalesloftAPIKeyMutation,
  useDisconnectIntegrationMutation,
} = enhancedApiSlice;
